import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ReferenceLine,
  PieChart, Pie,
} from "recharts";
import {
  Wallet, Home, PartyPopper, CalendarClock, PiggyBank, TrendingUp, AlertTriangle,
  CheckCircle2, Printer, RotateCcw, Pencil, SlidersHorizontal,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { calculate, fmt, PIE_COLORS } from "@/lib/budget";
import Tips from "@/components/Tips";

const STATUS = {
  green: { bg: "bg-[#EAF3E9]", ring: "ring-[#377D30]/25", text: "text-[#377D30]", label: "You're in good shape" },
  amber: { bg: "bg-[#FFF3E0]", ring: "ring-[#FF8700]/25", text: "text-[#ED6B00]", label: "It's a little tight" },
  red: { bg: "bg-[#FDECEC]", ring: "ring-[#ED0000]/25", text: "text-[#ED0000]", label: "This won't stretch far enough" },
};

const SummaryCard = ({ icon: Icon, label, value, tone = "default", testId }) => {
  const tones = {
    default: "text-[#1A1A1A]",
    good: "text-[#377D30]",
    bad: "text-[#ED0000]",
  };
  return (
    <div data-testid={testId} className="rounded-2xl border border-[#E8EAEF] bg-white p-5 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
      <div className="mb-2 flex items-center gap-2 text-[#747D89]">
        <Icon size={16} />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className={`font-display text-2xl font-semibold ${tones[tone]}`}>{value}</p>
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-[#E8EAEF] bg-white px-3 py-2 text-xs shadow-md">
      <p className="font-semibold text-[#1A1A1A]">{label || d.name}</p>
      {d.balance !== undefined && <p className="text-[#747D89]">Balance: {fmt(d.balance)}</p>}
      {d.value !== undefined && <p className="text-[#747D89]">{fmt(d.value)}</p>}
    </div>
  );
};

export default function Dashboard({ state, onEdit, onReset }) {
  const [whatIf, setWhatIf] = useState({ rent: 0, food: 0, partTime: 0, extraShift: 0 });
  const r = useMemo(() => calculate(state, whatIf), [state, whatIf]);
  const st = STATUS[r.status];
  const hasWhatIf = !!(whatIf.rent || whatIf.food || whatIf.partTime || whatIf.extraShift);

  const sliders = [
    { key: "rent", label: "Rent", min: -100, max: 200, step: 10 },
    { key: "food", label: "Food", min: -80, max: 150, step: 5 },
    { key: "partTime", label: "Part-time income", min: -100, max: 400, step: 10 },
    { key: "extraShift", label: "Extra shift (monthly)", min: 0, max: 200, step: 10 },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 pt-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A] sm:text-4xl">Your budget dashboard</h1>
          <p className="mt-1 text-[#747D89]">Here's how your academic year looks, spread across {r.timeline.length} months.</p>
        </div>
        <div className="no-print flex gap-2">
          <Button variant="outline" data-testid="edit-budget-btn" onClick={onEdit} className="rounded-full border-[#E8EAEF]">
            <Pencil size={16} className="mr-2" /> Edit
          </Button>
          <Button variant="outline" data-testid="print-btn" onClick={() => window.print()} className="rounded-full border-[#E8EAEF]">
            <Printer size={16} className="mr-2" /> Print / PDF
          </Button>
          <Button data-testid="reset-btn" onClick={onReset} className="rounded-full bg-[#0F64F8] text-white hover:bg-[#134192]">
            <RotateCcw size={16} className="mr-2" /> Start over
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <SummaryCard testId="summary-income" icon={Wallet} label="Income this year" value={fmt(r.annualIncome)} />
        <SummaryCard testId="summary-living" icon={Home} label="Living costs" value={fmt(r.livingAnnual)} />
        <SummaryCard testId="summary-social" icon={PartyPopper} label="Social spending" value={fmt(r.socialAnnual)} />
        <SummaryCard testId="summary-oneoff" icon={CalendarClock} label="One-off costs" value={fmt(r.oneOffTotal)} />
        <SummaryCard testId="summary-leftover" icon={PiggyBank} label="Left over" value={fmt(r.leftover)} tone={r.leftover >= 0 ? "good" : "bad"} />
      </div>

      {/* Safe spending + What if */}
      <div className="mt-6 grid gap-6 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
          data-testid="safe-spending-card"
          className={`lg:col-span-5 rounded-2xl border border-[#E8EAEF] p-6 ring-1 ${st.bg} ${st.ring}`}
        >
          <p className={`text-sm font-semibold ${st.text}`}>{st.label}</p>
          <p className="mt-3 text-sm text-[#747D89]">You can safely spend</p>
          <p className={`font-display text-5xl font-bold ${st.text}`} data-testid="safe-weekly">{fmt(Math.max(0, r.safeWeekly))}<span className="text-lg font-medium"> / week</span></p>
          <p className="mt-1 text-[#747D89]">or <span className="font-semibold text-[#1A1A1A]" data-testid="safe-monthly">{fmt(Math.max(0, r.safeMonthly))}</span> per month</p>
          <p className="mt-3 text-sm text-[#747D89]">without running out before your next payment.</p>
        </motion.div>

        <div className="no-print lg:col-span-7 rounded-2xl border border-[#E8EAEF] bg-white p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
          <div className="mb-4 flex items-center gap-2">
            <SlidersHorizontal size={18} className="text-[#734FFC]" />
            <h3 className="text-lg font-semibold text-[#1A1A1A]">What if?</h3>
            {hasWhatIf && (
              <button data-testid="whatif-reset" onClick={() => setWhatIf({ rent: 0, food: 0, partTime: 0, extraShift: 0 })} className="ml-auto text-xs font-medium text-[#734FFC] underline">
                reset
              </button>
            )}
          </div>
          <div className="space-y-4">
            {sliders.map((s) => (
              <div key={s.key}>
                <div className="mb-1.5 flex items-center justify-between text-sm">
                  <span className="font-medium text-[#1A1A1A]">{s.label}</span>
                  <span className="font-semibold text-[#734FFC]" data-testid={`whatif-value-${s.key}`}>
                    {whatIf[s.key] > 0 ? "+" : ""}{fmt(whatIf[s.key])}
                  </span>
                </div>
                <Slider
                  data-testid={`whatif-slider-${s.key}`}
                  value={[whatIf[s.key]]}
                  min={s.min} max={s.max} step={s.step}
                  onValueChange={([v]) => setWhatIf((w) => ({ ...w, [s.key]: v }))}
                />
              </div>
            ))}
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 rounded-xl bg-[#FBFAF7] p-4">
            <div>
              <p className="text-xs text-[#747D89]">Safe weekly spend</p>
              <p className="font-display text-xl font-semibold text-[#377D30]">{fmt(Math.max(0, r.safeWeekly))}</p>
            </div>
            <div>
              <p className="text-xs text-[#747D89]">End-of-year balance</p>
              <p className={`font-display text-xl font-semibold ${r.leftover >= 0 ? "text-[#377D30]" : "text-[#ED0000]"}`}>{fmt(r.leftover)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline + Pie */}
      <div className="mt-6 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7 rounded-2xl border border-[#E8EAEF] bg-white p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
          <div className="mb-1 flex items-center gap-2">
            <TrendingUp size={18} className="text-[#734FFC]" />
            <h3 className="text-lg font-semibold text-[#1A1A1A]">Cash flow timeline</h3>
          </div>
          <p className="mb-4 text-sm text-[#747D89]">Your running balance month by month — see why September feels rich and December feels tight.</p>
          <div style={{ width: "100%", height: 260 }} data-testid="cashflow-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={r.timeline} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#747D89" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#747D89" }} axisLine={false} tickLine={false} tickFormatter={(v) => `£${v}`} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(58,90,64,0.05)" }} />
                <ReferenceLine y={0} stroke="#ED0000" strokeDasharray="3 3" />
                <Bar dataKey="balance" radius={[6, 6, 0, 0]} isAnimationActive>
                  {r.timeline.map((d, i) => (
                    <Cell key={i} fill={d.balance < 0 ? "#ED0000" : d.payday ? "#734FFC" : "#C7BAFC"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-5 rounded-2xl border border-[#E8EAEF] bg-white p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
          <h3 className="mb-4 text-lg font-semibold text-[#1A1A1A]">Spending breakdown</h3>
          {r.breakdown.length ? (
            <div className="flex flex-col items-center gap-4 sm:flex-row" data-testid="breakdown-chart">
              <div style={{ width: 160, height: 160 }} className="shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={r.breakdown} dataKey="value" nameKey="name" innerRadius={42} outerRadius={70} paddingAngle={2} isAnimationActive>
                      {r.breakdown.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="flex-1 space-y-1.5">
                {r.breakdown.map((d, i) => (
                  <li key={d.name} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-[#747D89]">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      {d.name}
                    </span>
                    <span className="font-semibold text-[#1A1A1A]">{Math.round((d.value / r.totalSpend) * 100)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-[#747D89]">Add some spending to see your breakdown.</p>
          )}
        </div>
      </div>

      {/* Warnings & positives */}
      <div className="mt-6 space-y-3" data-testid="messages">
        {r.warnings.map((w, i) => (
          <div key={i} data-testid="warning-item" className="flex items-start gap-3 rounded-xl border border-[#F7CFCF] bg-[#FDECEC] p-4 text-sm text-[#B71C1C]">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[#ED0000]" />
            <span>{w}</span>
          </div>
        ))}
        {r.positives.map((p, i) => (
          <div key={i} data-testid="positive-item" className="flex items-start gap-3 rounded-xl border border-[#CFE3CD] bg-[#EAF3E9] p-4 text-sm text-[#2E6A28]">
            <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[#377D30]" />
            <span>{p}</span>
          </div>
        ))}
      </div>

      <Tips />
    </div>
  );
}
